import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = ["IN-PERSON", "VIRTUAL (ZOOM)", "EITHER"];

const inputCls =
  "w-full bg-transparent border border-blue-100/15 px-4 py-3.5 text-base font-light text-white placeholder:text-blue-100/30 focus:border-[#4F7CFF] focus:outline-none transition-colors duration-300";
const labelCls = "font-meta text-[10px] tracking-[0.3em] text-blue-300/60 block mb-2.5";

export default function Book() {
  const [form, setForm] = useState({
    org_name: "", organizer_name: "", email: "", phone: "",
    event_date: "", format: "IN-PERSON", location: "",
    audience: "", topic: "", details: "", website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const ready =
    form.org_name.trim() && form.organizer_name.trim() && form.email.includes("@");

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        phone: form.phone || null,
        event_date: form.event_date || null,
        location: form.location || null,
        audience: form.audience || null,
        topic: form.topic || null,
        details: form.details || null,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      setError("Something failed to send. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="relative bg-[#05070F] text-[#F8F8F8] min-h-screen" data-testid="book-page">
      <div className="aura-field" aria-hidden="true" />
      <div className="relative z-10">
        <header className="flex justify-between items-center px-6 sm:px-12 py-5 border-b border-blue-100/10">
          <Link to="/" data-testid="book-back-home" className="font-display font-extrabold text-sm tracking-tight text-aura-gradient">
            JW.
          </Link>
          <Link
            to="/speaking"
            data-testid="book-speaking-link"
            className="font-meta text-[10px] sm:text-xs tracking-[0.25em] text-blue-100/60 hover:text-white transition-colors duration-300"
          >
            ← THE SPEAKING PAGE
          </Link>
        </header>

        <div className="max-w-2xl mx-auto px-6 py-14 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            {done ? (
              <div className="border border-[#4F7CFF] p-8 sm:p-14 text-center" data-testid="book-success">
                <p className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl">
                  Request received.
                </p>
                <p className="text-sm sm:text-base font-light text-blue-100/60 mt-4">
                  Thank you — Jeremiah reads every request personally and will get back to you
                  within a few days at the contact info you gave.
                </p>
                <Link
                  to="/"
                  data-testid="book-success-home"
                  className="inline-block mt-8 font-meta text-xs tracking-[0.25em] border border-blue-200/30 rounded-full px-6 py-3 hover:bg-[#4F7CFF] hover:border-[#4F7CFF] transition-colors duration-300"
                >
                  ← BACK TO THE SITE
                </Link>
              </div>
            ) : (
              <>
                <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl" data-testid="book-heading">
                  Book Jeremiah.
                </h1>
                <p className="text-sm sm:text-base font-light text-blue-100/60 mt-3">
                  Schools, youth programs, community organizations, conferences, churches, panels —
                  tell us about your event and Jeremiah will get back to you.
                </p>

                <div className="mt-10 space-y-7">
                  <div>
                    <label className={labelCls} htmlFor="book-org">ORGANIZATION OR SCHOOL NAME *</label>
                    <input id="book-org" value={form.org_name} onChange={set("org_name")} placeholder="e.g. Eastside High School" className={inputCls} data-testid="book-org-input" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label className={labelCls} htmlFor="book-name">YOUR NAME *</label>
                      <input id="book-name" value={form.organizer_name} onChange={set("organizer_name")} placeholder="Organizer's name" className={inputCls} data-testid="book-name-input" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="book-email">YOUR EMAIL *</label>
                      <input id="book-email" type="email" value={form.email} onChange={set("email")} placeholder="you@organization.org" className={inputCls} data-testid="book-email-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label className={labelCls} htmlFor="book-phone">PHONE (OPTIONAL)</label>
                      <input id="book-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className={inputCls} data-testid="book-phone-input" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="book-date">EVENT DATE</label>
                      <input id="book-date" value={form.event_date} onChange={set("event_date")} placeholder="e.g. March 14, 2027" className={inputCls} data-testid="book-date-input" />
                    </div>
                  </div>

                  <div>
                    <span className={labelCls}>FORMAT</span>
                    <div className="flex flex-wrap gap-2" data-testid="book-format-picker">
                      {FORMATS.map((f) => (
                        <button
                          key={f}
                          onClick={() => setForm((p) => ({ ...p, format: f }))}
                          data-testid={`book-format-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`font-meta text-[10px] tracking-[0.2em] border px-4 py-2 transition-colors duration-200 ${
                            form.format === f
                              ? "bg-[#4F7CFF] border-[#4F7CFF] text-white"
                              : "border-blue-100/15 text-blue-100/60 hover:border-blue-100/40"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="book-location">EVENT LOCATION (IF IN-PERSON)</label>
                    <input id="book-location" value={form.location} onChange={set("location")} placeholder="City, venue" className={inputCls} data-testid="book-location-input" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    <div>
                      <label className={labelCls} htmlFor="book-audience">EXPECTED AUDIENCE</label>
                      <input id="book-audience" value={form.audience} onChange={set("audience")} placeholder="e.g. 200 high-school students" className={inputCls} data-testid="book-audience-input" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="book-topic">REQUESTED TOPIC</label>
                      <input id="book-topic" value={form.topic} onChange={set("topic")} placeholder="e.g. Critical thinking & social media" className={inputCls} data-testid="book-topic-input" />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="book-details">ANYTHING ELSE ABOUT THE EVENT</label>
                    <textarea id="book-details" value={form.details} onChange={set("details")} rows={6} placeholder="Schedule, theme of the event, what you want the audience to walk away with…" className={`${inputCls} resize-y leading-relaxed`} data-testid="book-details-input" />
                  </div>

                  <input
                    type="text"
                    value={form.website}
                    onChange={set("website")}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                    data-testid="book-honeypot"
                  />

                  <button
                    onClick={submit}
                    disabled={!ready || busy}
                    data-testid="book-submit-button"
                    className={`w-full font-meta text-xs tracking-[0.3em] py-4 border transition-colors duration-300 ${
                      ready && !busy
                        ? "bg-[#4F7CFF] border-[#4F7CFF] text-white hover:bg-white hover:border-white hover:text-black"
                        : "border-blue-100/15 text-blue-100/30 cursor-not-allowed"
                    }`}
                  >
                    {busy ? "SENDING…" : "SEND BOOKING REQUEST"}
                  </button>

                  {error && (
                    <p className="font-meta text-xs tracking-[0.2em] text-red-400 border border-red-400/30 px-4 py-3" data-testid="book-error">
                      {error}
                    </p>
                  )}

                  <p className="font-meta text-[9px] tracking-[0.25em] text-blue-200/30 text-center">
                    * REQUIRED. YOUR REQUEST GOES STRAIGHT TO JEREMIAH.
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </main>
  );
}
